import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  FileText,
  Building2,
  Download,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Briefcase,
  Banknote,
  FileCheck,
  PieChart,
  ArrowRight,
  FileSpreadsheet,
  Clock,
  Lock,
} from "lucide-react";
import { formatChf } from "../shared/onboarding-types";

type LoanType =
  | "working_capital"
  | "equipment"
  | "real_estate"
  | "expansion"
  | "refinancing";

interface DocumentItem {
  id: string;
  name: string;
  description: string;
  status: "ready" | "pending" | "missing" | "generating";
  required: boolean;
}

const LOAN_TYPES: { value: LoanType; label: string; description: string }[] = [
  {
    value: "working_capital",
    label: "Working Capital",
    description: "Short-term financing for operational needs",
  },
  {
    value: "equipment",
    label: "Equipment Financing",
    description: "Purchase of machinery or equipment",
  },
  {
    value: "real_estate",
    label: "Real Estate",
    description: "Commercial property purchase or development",
  },
  {
    value: "expansion",
    label: "Business Expansion",
    description: "Growth financing for market expansion",
  },
  {
    value: "refinancing",
    label: "Debt Refinancing",
    description: "Consolidate or restructure existing debt",
  },
];

export default function LoanApplicationGenerator() {
  const { selectedCompanyId } = useCompany();
  const [loanType, setLoanType] = useState<LoanType>("working_capital");
  const [loanAmount, setLoanAmount] = useState<string>("100000");
  const [purpose, setPurpose] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);

  // Query for company data
  const { data: company, isLoading: isLoadingCompany } =
    trpc.company.get.useQuery(
      { id: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  // Mock documents that would be generated
  const documents: DocumentItem[] = [
    {
      id: "balance_sheet",
      name: "Balance Sheet (Last 3 Years)",
      description:
        "Historical balance sheets showing assets, liabilities, and equity",
      status: "ready",
      required: true,
    },
    {
      id: "income_statement",
      name: "Income Statement (Last 3 Years)",
      description: "Profit and loss statements showing revenue and expenses",
      status: "ready",
      required: true,
    },
    {
      id: "cash_flow",
      name: "Cash Flow Statement",
      description:
        "Statement of cash flows for operating, investing, and financing activities",
      status: "ready",
      required: true,
    },
    {
      id: "tax_returns",
      name: "Tax Returns (Last 2 Years)",
      description: "Filed corporate tax returns with canton and federal stamps",
      status: "pending",
      required: true,
    },
    {
      id: "bank_statements",
      name: "Bank Statements (Last 6 Months)",
      description: "Recent bank account activity showing cash position",
      status: "ready",
      required: true,
    },
    {
      id: "aged_receivables",
      name: "Aged Receivables Report",
      description: "Outstanding customer invoices by age bucket",
      status: "ready",
      required: false,
    },
    {
      id: "aged_payables",
      name: "Aged Payables Report",
      description: "Outstanding supplier invoices by age bucket",
      status: "ready",
      required: false,
    },
    {
      id: "budget_forecast",
      name: "Budget & Forecast",
      description: "Projected financial performance for the next 2-3 years",
      status: "missing",
      required: true,
    },
    {
      id: "business_plan",
      name: "Business Plan Summary",
      description: "Overview of business strategy and loan utilization plan",
      status: "missing",
      required: true,
    },
    {
      id: "company_registry",
      name: "Commercial Registry Extract",
      description: "Official company registration documents",
      status: "pending",
      required: true,
    },
  ];

  const readyCount = documents.filter(d => d.status === "ready").length;
  const totalRequired = documents.filter(d => d.required).length;
  const completionPercentage = Math.round(
    (readyCount / documents.length) * 100
  );

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationStep(1);

    // Simulate generation steps
    for (let i = 1; i <= 5; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setGenerationStep(i);
    }

    setIsGenerating(false);
    // In reality, this would generate a ZIP file with all documents
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
            <p className="text-muted-foreground">
              Please select a company to generate loan application documents.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoadingCompany) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Briefcase className="h-8 w-8 text-primary" />
              Loan Application Generator
            </h1>
            <p className="text-muted-foreground mt-2">
              Generate a complete loan application package for Swiss banks
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Configuration Panel */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Loan Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input value={company?.name || ""} disabled />
                  </div>

                  <div className="space-y-2">
                    <Label>Loan Type</Label>
                    <Select
                      value={loanType}
                      onValueChange={v => setLoanType(v as LoanType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LOAN_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {LOAN_TYPES.find(t => t.value === loanType)?.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Requested Amount (CHF)</Label>
                    <Input
                      type="number"
                      value={loanAmount}
                      onChange={e => setLoanAmount(e.target.value)}
                      min="0"
                      step="10000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Purpose of Loan</Label>
                    <Textarea
                      placeholder="Describe how the loan will be used..."
                      value={purpose}
                      onChange={e => setPurpose(e.target.value)}
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Readiness Score
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center">
                    <div className="relative w-24 h-24">
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          className="text-muted stroke-current"
                          strokeWidth="10"
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                        />
                        <circle
                          className={`stroke-current ${completionPercentage >= 80 ? "text-success" : completionPercentage >= 50 ? "text-warning" : "text-destructive"}`}
                          strokeWidth="10"
                          strokeLinecap="round"
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          strokeDasharray={`${completionPercentage * 2.51} ${100 * 2.51}`}
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-2xl font-bold">
                          {completionPercentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center text-sm text-muted-foreground">
                    {readyCount} of {documents.length} documents ready
                  </div>
                  <div className="pt-4 border-t">
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={handleGenerate}
                      disabled={isGenerating || completionPercentage < 50}
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Generate Package
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Documents List */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Required Documents
                  </CardTitle>
                  <CardDescription>
                    Documents that will be included in the loan application
                    package
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {documents.map(doc => (
                      <div
                        key={doc.id}
                        className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="mt-0.5">
                          {doc.status === "ready" && (
                            <CheckCircle className="h-5 w-5 text-success" />
                          )}
                          {doc.status === "pending" && (
                            <Clock className="h-5 w-5 text-warning" />
                          )}
                          {doc.status === "missing" && (
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                          )}
                          {doc.status === "generating" && (
                            <Loader2 className="h-5 w-5 text-primary animate-spin" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{doc.name}</h4>
                            {doc.required && (
                              <Badge variant="outline" className="text-xs">
                                Required
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {doc.description}
                          </p>
                        </div>
                        <div>
                          {doc.status === "ready" && (
                            <Badge className="bg-success/10 text-success">
                              Ready
                            </Badge>
                          )}
                          {doc.status === "pending" && (
                            <Badge className="bg-warning/10 text-warning">
                              Pending
                            </Badge>
                          )}
                          {doc.status === "missing" && (
                            <Badge className="bg-destructive/10 text-destructive">
                              Missing
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Generation Progress */}
              {isGenerating && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileSpreadsheet className="h-5 w-5" />
                      Generating Package
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Progress value={generationStep * 20} className="h-2" />
                    <div className="space-y-2">
                      {[
                        "Collecting financial statements...",
                        "Generating cash flow analysis...",
                        "Creating financial ratios summary...",
                        "Compiling supporting documents...",
                        "Finalizing package...",
                      ].map((step, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 text-sm ${idx < generationStep ? "text-foreground" : "text-muted-foreground"}`}
                        >
                          {idx < generationStep ? (
                            <CheckCircle className="h-4 w-4 text-success" />
                          ) : idx === generationStep ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <div className="h-4 w-4 rounded-full border" />
                          )}
                          {step}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Swiss Bank Requirements Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Swiss Bank Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 text-sm">
                    <p className="text-muted-foreground">
                      Swiss banks typically require the following for business
                      loan applications:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                      <li>
                        Financial statements for the past 3 years (audited if
                        available)
                      </li>
                      <li>Recent bank statements showing cash flow</li>
                      <li>Tax returns and compliance certificates</li>
                      <li>Business plan with financial projections</li>
                      <li>Collateral documentation (if applicable)</li>
                      <li>
                        Commercial registry extract (Handelsregisterauszug)
                      </li>
                    </ul>
                    <p className="text-muted-foreground pt-2 border-t">
                      This generator prepares documents in formats accepted by
                      major Swiss banks including UBS, Credit Suisse,
                      Raiffeisen, and cantonal banks.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
